<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol lg="3">
                <DInput
                    id="search"
                    v-model="filterData.orderUuid"
                    :label="__('orders.search_order_number')"
                    :is-valid="searchIsValid"
                    :invalid-feedback="searchError"
                    @keydown.enter.exact.prevent="search"
                />
            </DCol>
            <DCol lg="3">
                <DCol sm="7">
                    <DMultiSelect
                        id="order-statuses-dropdown"
                        :options="getSelectStatuses"
                        :search="true"
                        :selection="true"
                        :selected="filterData.statuses"
                        :options-empty-placeholder="__('orders.no_status')"
                        selection-type="text"
                        :label="__('orders.search_status')"
                        @update="updateSelectedStatuses"
                    />
                </DCol>
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('orders.search')"
                    @click="search(orderCollection.page)"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
            <DCol
                sm="3"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCol lg="12">
                <DCard
                    class="orders-box"
                >
                    <template #default>
                        <DTable
                            :fields="getFields"
                            class="table-responsive"
                            :items="orderCollection.orders.concat(orderCollection.invalidOrders)"
                        >
                            <template #orderNumber="{item}">
                                <td>{{ item.orderNumber }} </td>
                                <td
                                    v-if="!item.valid"
                                    :colspan="colspan"
                                >
                                    <DTag
                                        :label="__('orders.invalid_order')"
                                        color="danger"
                                    /> {{ item.error }}
                                </td>
                            </template>
                            <template #entryDate="{item}">
                                <td v-if="item.valid && item.dateCreated">
                                    {{ setDateFormat(item.dateCreated) }}
                                </td>
                            </template>
                            <template #status="{item}">
                                <td v-if="item.valid">
                                    {{ item.status }}
                                </td>
                            </template>
                            <template #minFraudRecommendation="{item}">
                                <td v-if="item.valid && item.minFraudRecommendation">
                                    <DTag
                                        :label="__('orders.'+item.minFraudRecommendation)"
                                        :color="item.minFraudRecommendation !== 'accept' ? 'danger' : 'secondary'"
                                    />
                                    <br><span
                                        v-if="item.minFraudScore"
                                        style="display: inline-block; padding-top: 5px; font-size: 12px;"
                                    >({{ item.minFraudScore }})</span>
                                    <br v-if="isPrepaidOrderWithInvoicingDisabled(item)">
                                    <DTag
                                        v-if="isPrepaidOrderWithInvoicingDisabled(item)"
                                        :label="__('orders.invoicing_disabled')"
                                        color="danger"
                                    />
                                </td>
                                <td v-if="item.valid && !item.minFraudRecommendation" />
                            </template>
                            <template #customer="{item}">
                                <td
                                    v-if="item.valid && item.customer"
                                    :style="isOrderOpen(item) && !item.customer.id && item.databaseCustomers.length > 0 ? 'background-color: #ffff66; color: #000000' : ''"
                                >
                                    <div v-if="item.customer.company">
                                        {{ item.customer.company }}
                                    </div>
                                    <div v-if="item.customer.firstname || item.customer.lastname">
                                        <span v-if="item.customer.firstname">{{ item.customer.firstname }}</span>
                                        <span v-if="item.customer.lastname">{{ item.customer.lastname }}</span>
                                    </div>
                                    <p v-if="item.customer.id && item.customer.status !== 'active'">
                                        <DTag
                                            :label="__('orders.inactive')"
                                            color="danger"
                                        />
                                    </p>
                                    <p v-if="item.customerId">
                                        <Link :href="route('customers.customers.show', {customer: item.customerId})">
                                            {{ item.customerId }}
                                        </Link>
                                    </p>
                                    <div
                                        v-if="isOrderOpen(item) && !item.customer?.id && item.databaseCustomers.length > 0 && userPermissions.includes('order.accept')"
                                        style="margin-top: 10px;"
                                    >
                                        <DButton
                                            mini
                                            :label="__('orders.assign_customer')"
                                            @click="displayAssignCustomerDialog(item)"
                                        />
                                    </div>
                                </td>
                                <td v-if="item.valid && !item.customer" />
                            </template>
                            <template #products="{item}">
                                <td v-if="item.valid">
                                    <p
                                        v-for="(orderItem, productIndex) in item.items"
                                        :key="productIndex"
                                    >
                                        - <strong>{{ orderItem.domainName !== null ? orderItem.domainName : orderItem.name }}</strong>
                                    </p>
                                </td>
                            </template>
                            <template #payment="{item}">
                                <td v-if="item.valid">
                                    {{ item.totalPayment }}<br>
                                    <DTag
                                        v-if="item.customer"
                                        :label="'via ' + item.customer.payment_method"
                                    />
                                </td>
                            </template>
                            <template #orderNote="{item}">
                                <td
                                    v-if="item.orderNote"
                                    class="text-break"
                                >
                                    <span
                                        v-if="item.orderNote.note.length > 50"
                                    >
                                        {{ item.orderNote.note.substring(0, 50) }} ...<a
                                            href="javascript:void(0);"
                                            @click="showNote(item)"
                                        >{{ __('orders.more') }}</a>
                                    </span>
                                    <span v-else>
                                        {{ item.orderNote.note }}
                                    </span>
                                </td>
                                <td v-else />
                            </template>
                            <template #actions="{item}">
                                <td style="display: flex; grid-gap: 5px;">
                                    <DButton
                                        mini
                                        icon="eye"
                                        @click="orderDetail(item)"
                                    />
                                    <DDropdown
                                        toggler-text="..."
                                        color="secondary"
                                        :caret="false"
                                    >
                                        <DDropdownItem
                                            v-if="userPermissions.includes('order.reject') && item.status === 'open'"
                                            @click="rejectOrder(item, false)"
                                        >
                                            {{ __('orders.reject') }}
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="userPermissions.includes('order.reject') && item.status === 'open'"
                                            @click="rejectOrder(item, true)"
                                        >
                                            {{ __('orders.silent_reject') }}
                                        </DDropdownItem>

                                        <span
                                            v-if="userPermissions.includes('order.accept') && item.status === 'open' && item.customerId !== null"
                                        >
                                            <DDropdownItem
                                                v-if="item.valid"
                                                @click="acceptOrder(item)"
                                            >
                                                {{ __('orders.accept') }}
                                            </DDropdownItem>
                                        </span>
                                        <DDropdownItem
                                            v-if="(!item.customer || !item.customer.id) && (item.databaseCustomers && item.databaseCustomers.length > 0) && item.status === 'open'"
                                            @click="displayAssignCustomerDialog(item)"
                                        >
                                            {{ __('orders.assign_customer') }}
                                        </DDropdownItem>
                                        <DDropdownItem
                                            @click="showOrderNoteDialog(item)"
                                        >
                                            {{ __('orders.note.label') }}
                                        </DDropdownItem>
                                    </DDropdown>
                                </td>
                            </template>
                        </DTable>
                        <DPagination
                            :active-page="orderCollection.page"
                            :pages="orderCollection.totalPages"
                            align="center"
                            @update:activePage="search"
                        />
                        <DModal
                            v-model:show="showRejectDialog"
                            :title="dialogTitle"
                            :message="dialogMessage"
                            is-confirm
                            is-error
                            @close="showRejectDialog = false"
                            @confirm="rejectCallback"
                        />
                        <DModal
                            v-model:show="showAcceptInvoiceDisabledDialog"
                            :title="__('orders.invoice_disabled_dialog.title')"
                            :message="__('orders.invoice_disabled_dialog.message')"
                            is-error
                            @close="showAcceptInvoiceDisabledDialog = false"
                        />
                        <DModal
                            v-model:show="showAcceptDialog"
                            :title="dialogTitle"
                            :message="dialogMessage"
                            is-confirm
                            @close="showAcceptDialog = false"
                            @confirm="acceptCallback"
                        />
                        <OrderNoteCreateModal
                            v-if="showNoteCreateModal"
                            :show-modal="showNoteCreateModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            :order-note="orderNoteDialog.currentNote"
                            :errors="$page.props.errors"
                            @close="showNoteCreateModal = false"
                            @confirm="saveOrderNote"
                        />
                        <DModal
                            :show="showNoteModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            is-big
                            @close="showNoteModal = false"
                        >
                            <div class="p-3">
                                <DFormTextarea
                                    :value="selectedItem.orderNote ? selectedItem.orderNote.note : ''"
                                    :disabled="true"
                                    rows="5"
                                />
                            </div>
                        </DModal>
                        <CustomerAssignDialogModal
                            v-if="showCustomerAssignDialog"
                            :show-modal="showCustomerAssignDialog"
                            :selected-item="selectedItem"
                            :selected-database-customer="selectedDatabaseCustomer"
                            :order-customer="orderCustomer"
                            :database-customers="databaseCustomers"
                            :is-one-webshop-order="true"
                            @confirm-modal="assignCustomerToOrder"
                            @close-modal="showCustomerAssignDialog = false"
                        />
                    </template>
                </DCard>
            </DCol>
        </DRow>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script setup>
import OrderNoteCreateModal from '~@/Components/Orders/OrderNoteCreateModal'
import { computed, ref } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { customerBankDetails as getCustomerBankDetails } from '~@/Composables/sepaMandate'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import CustomerAssignDialogModal from '~@/Components/Orders/CustomerAssignDialogModal'

const props = defineProps({
    orderCollection: {
        type: Object,
        default () {
            return {}
        }
    },
    errors: {
        type: Object,
        default () {
            return {}
        }
    },
    page: {
        type: Number,
        default: 1
    },
    orderClient: {
        type: String,
        default: ''
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    },
    orderStatuses: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const getSelectStatuses = computed(
    () => {
        return props.orderStatuses.map((item) => {
            return { value: item, text: item, selected: filterData.value.statuses.includes(item) }
        })
    }
)

const userPermissions = computed(() => usePage().props.auth.permissions)

const colspan = ref(5)
getColSpan()
const showListViewModal = ref(false)
const getFields = computed(() => {
    const availableList = []

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: __('settings.list_view.' + item.list + '.' + item.column),
                sorter: item.sort_column_enabled,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
            })
        }
    })

    getColSpan()

    return availableList
})

// Modals
const dialogTitle = ref('')
const dialogMessage = ref('')

// Accept order
const showAcceptDialog = ref(false)
const acceptCallback = ref(() => { return {} })

const acceptOrder = (order) => {
    const orderNumber = order.orderNumber
    if (isPrepaidOrderWithInvoicingDisabled(order)) {
        showAcceptInvoiceDisabledDialog.value = true
        return
    }

    dialogTitle.value = __('orders.accept_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.accept_dialog_message', { orderNumber })

    acceptCallback.value = () => {
        router.patch(
            route(
                'orders.one_webshop.accept', {
                    orderUuid: orderNumber,
                    _query: { page: props.page }
                }
            )
        )
    }

    showAcceptDialog.value = true
}

// Reject order
const showRejectDialog = ref(false)
const rejectCallback = ref(() => { return {} })

const rejectOrder = (order, silent) => {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.reject_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.reject_dialog_message', { orderNumber })

    rejectCallback.value = () => {
        router.patch(route(
            'orders.one_webshop.reject',
            {
                orderUuid: orderNumber,
                query: { page: props.page }
            }
        ), { silent: silent })
    }

    showRejectDialog.value = true
}

// Set order note
const selectedItem = ref({})
const showNoteModal = ref(false)
const showNoteCreateModal = ref(false)
const orderNoteDialog = ref({
    currentNote: {}
})
const showAcceptInvoiceDisabledDialog = ref(false)

const showOrderNoteDialog = (order) => {
    selectedItem.value = order
    showNoteCreateModal.value = true

    orderNoteDialog.value.currentNote = order.orderNote ?? {
        id: null, // new note
        order_id: order.id,
        oder_no: order.orderNumber,
        note: ''
    }
}

const saveOrderNote = (form) => {
    const requestMethod = form.id !== null ? 'patch' : 'post'
    const routeName = form.id !== null ? 'ordernotes.update' : 'ordernotes.create'
    const routeParameters = form.id !== null
        ? {
            orderClient: props.orderClient,
            orderNote: form.id,
            _query: { page: props.page }
        }
        : {
            orderClient: props.orderClient,
            orderId: selectedItem.value.id,
            orderNumber: selectedItem.value.orderNumber,
            _query: { page: props.page }
        }

    form[requestMethod](route(routeName, routeParameters), {
        onSuccess: () => {
            showNoteCreateModal.value = false
        }
    })
}

// Show order note
const showNote = (order) => {
    selectedItem.value = order
    showNoteModal.value = true
}

// Search and show orders
const filterData = ref({
    orderUuid: props.filters.orderUuid,
    statuses: props.filters.statuses,
    manual_search_mode: true
})

const updateSelectedStatuses = (values) => {
    filterData.value.statuses = values
}

const searchError = computed(() => props.errors.search ?? __('orders.search_error'))

const searchIsValid = (value) => {
    if (value.length === 0) {
        return null
    }

    return value.trim().length > 0
}
const search = (page) => {
    const queryParameters = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))

    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route(
        'orders.one_webshop.index',
        {
            _query: queryParameters
        }
    ))
}
const resetFilters = () => {
    router.get(route(
        'orders.one_webshop.index',
        { _query: { page: props.page, manual_search_mode: false } }))
}

// Order details
const orderDetail = (item) => {
    router.get(route('orders.details', { order: item.orderNumber, client: 'OneWebshop' }))
}

function getColSpan () {
    let activeListLength = 0

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            ++activeListLength
        }
    })

    const colspanData = activeListLength - 5

    if (colspanData < 0) {
        colspan.value = 0

        return
    }

    if (colspanData < 5) {
        colspan.value = colspanData + 2

        return
    }

    colspan.value = colspanData
}

function isPrepaidOrderWithInvoicingDisabled (order) {
    return order.prepaid === true &&
    (order.customer.billing_type === 'no-invoice' || order.customer.brand.disable_invoicing === true)
}

// assign customer

const orderCustomer = ref({})
const databaseCustomers = ref([])
const selectedDatabaseCustomer = ref({})
const showCustomerAssignDialog = ref(false)

function displayAssignCustomerDialog (item) {
    selectedItem.value = item
    orderCustomer.value = item.customer
    databaseCustomers.value = item.databaseCustomers
    selectedDatabaseCustomer.value = item.databaseCustomers[0]
    selectedDatabaseCustomer.value.bankDetails = getCustomerBankDetails(selectedDatabaseCustomer.value)
    showCustomerAssignDialog.value = true
}

function assignCustomerToOrder (item) {
    router.post(
        route('orders.one_webshop.assign_customer', {
            orderUuid: item.order.orderNumber,
            customerId: item.customerId
        }),
        {
            customer_type: item.finalCustomer.customer_type,
            brand_id: item.finalCustomer.brand_id,
            title: item.finalCustomer.title,
            firstname: item.finalCustomer.firstname,
            lastname: item.finalCustomer.lastname,
            company: item.finalCustomer.company,
            address1: item.finalCustomer.address1,
            address2: item.finalCustomer.address2,
            postal_code: item.finalCustomer.postal_code,
            city: item.finalCustomer.city,
            country_code: item.finalCustomer.country_code,
            birthday: item.finalCustomer.birthday,
            phone: item.finalCustomer.phone,
            payment_method: item.finalCustomer.payment_method,
            payment_iban: item.finalCustomer.payment_iban,
            payment_bic: item.finalCustomer.payment_bic,
            account_owner: item.finalCustomer.account_owner,
            invoice_delivery_type_id: item.finalCustomer.invoice_delivery_type_id,
            electronic_invoice_id: item.finalCustomer.electronic_invoice_id,
            electronic_invoicing_type: item.finalCustomer.electronic_invoicing_type
        }
    )

    showCustomerAssignDialog.value = false
}

function isOrderOpen (item) {
    return item.status === 'open'
}
</script>
